import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, Hammer, Palette, Users, Sparkles, Clock, Award } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import useSeo from "../hooks/useSeo";

// Small inline store badges (placeholders — real app links added later)
const AppleBadge = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true"><path d="M16.365 1.43c0 1.14-.417 2.2-1.11 3.02-.83.98-2.2 1.74-3.34 1.65-.14-1.11.42-2.29 1.06-3.02.72-.83 1.98-1.45 3.03-1.5.02.09.03.19.03.28.02-.14.02-.28.03-.43zM20.5 17.02c-.55 1.27-.82 1.84-1.53 2.96-.99 1.56-2.39 3.5-4.12 3.51-1.54.02-1.94-1-4.03-.99-2.09.01-2.53 1.01-4.07.99-1.73-.02-3.05-1.77-4.04-3.33C-.2 15.9-.6 10.6 1.86 7.82c.98-1.11 2.4-1.81 3.94-1.81 1.57 0 2.56 1 3.86 1 1.26 0 2.03-1 3.85-1 1.36 0 2.8.74 3.83 2.02-3.36 1.84-2.81 6.64.16 8.19z"/></svg>
);
const PlayBadge = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true"><path d="M3.6 1.8c-.3.32-.48.8-.48 1.44v17.52c0 .64.18 1.12.48 1.44l.06.06L13.5 12.3v-.24L3.66 1.74l-.06.06zM17.1 15.9l-3.3-3.3v-.24l3.3-3.3 3.9 2.22c1.12.63 1.12 1.68 0 2.34l-3.9 2.28zM13.8 12.6l3.3 3.3-9.9 5.64c-.42.24-.84.24-1.14.06l7.74-9zM5.7 2.16c.3-.18.72-.18 1.14.06l9.9 5.64-3.3 3.3-7.74-9z"/></svg>
);

const StoreButton = ({ badge, top, main }) => (
  <a href="#" onClick={(e) => e.preventDefault()} className="group inline-flex items-center gap-3 bg-[#211d18] hover:bg-[#a67c52] text-white px-5 py-3 transition-colors min-w-[190px]" title="Coming soon">
    <span className="shrink-0">{badge}</span>
    <span className="flex flex-col leading-tight">
      <span className="text-[10px] uppercase tracking-[0.14em] text-white/70">{top}</span>
      <span className="text-[15px] font-medium">{main}</span>
    </span>
  </a>
);

const Academy = () => {
  const { tr, ui } = useLang();
  useSeo({
    title: ui.nav.academy,
    description: { en: "HAAS Academy — learn the ancient art of mosaic through immersive online lessons and hands-on projects with master mosaicists.", tr: "HAAS Akademi — usta mozaik sanatçılarıyla sürükleyici çevrimiçi dersler ve uygulamalı projeler eşliğinde antik mozaik sanatını öğrenin." },
  });

  const courses = [
    { icon: Hammer, image: "/mosaic/2025/08/3-1.jpg", title: { en: "Stone Cutting & Shaping", tr: "Taş Kesimi ve Şekillendirme" }, text: { en: "Master the fundamentals of cutting natural stone into precise tesserae.", tr: "Doğal taşı hassas tesseralara dönüştürmenin temellerini öğrenin." } },
    { icon: Palette, image: "/mosaic/2025/08/4.png", title: { en: "Colour & Composition", tr: "Renk ve Kompozisyon" }, text: { en: "Learn colour theory, gradients and the art of tonal harmony in mosaic.", tr: "Mozaikte renk teorisi, geçişler ve ton uyumu sanatını keşfedin." } },
    { icon: Sparkles, image: "/mosaic/2025/08/5.png", title: { en: "Hand-Setting Techniques", tr: "El ile Döşeme Teknikleri" }, text: { en: "Traditional andamento and setting methods used by master artisans.", tr: "Usta zanaatkârların kullandığı geleneksel andamento ve döşeme yöntemleri." } },
    { icon: Award, image: "/mosaic/2025/08/7.png", title: { en: "Finishing & Presentation", tr: "Son İşlem ve Sunum" }, text: { en: "Grouting, sealing and presenting a gallery-ready mosaic artwork.", tr: "Derz, koruma ve galeriye hazır bir mozaik eserin sunumu." } },
  ];

  const steps = [
    { icon: GraduationCap, title: { en: "Learn", tr: "Öğren" }, text: { en: "Step-by-step video lessons from master mosaicists.", tr: "Usta mozaik sanatçılarından adım adım video dersleri." } },
    { icon: Hammer, title: { en: "Practice", tr: "Uygula" }, text: { en: "Guided hands-on projects you complete at your own pace.", tr: "Kendi hızınızda tamamladığınız rehberli uygulamalı projeler." } },
    { icon: Users, title: { en: "Share", tr: "Paylaş" }, text: { en: "Join a community of artisans and get feedback on your work.", tr: "Zanaatkâr topluluğuna katılın ve çalışmalarınıza geri bildirim alın." } },
  ];

  const stats = [
    { icon: GraduationCap, value: "40+", label: { en: "Video Lessons", tr: "Video Ders" } },
    { icon: Clock, value: "20h", label: { en: "of Content", tr: "İçerik" } },
    { icon: Users, value: "2.500+", label: { en: "Students", tr: "Öğrenci" } },
    { icon: Award, value: "8", label: { en: "Certificates", tr: "Sertifika" } },
  ];

  return (
    <div className="bg-[#f7f3ee]">
      {/* Hero */}
      <section className="relative h-[85vh] min-h-[560px] w-full overflow-hidden bg-[#211d18]">
        <img src="/mosaic/academy/hero.jpg" alt="HAAS Academy" className="absolute inset-0 w-full h-full object-cover animate-kenburns" style={{ objectPosition: "center 45%" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/25" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 w-full">
            <div className="max-w-2xl">
              <p className="animate-fade-up text-[#e2c9a8] text-[12px] md:text-[13px] uppercase tracking-luxe mb-5">HAAS MOSAIC · {ui.nav.academy}</p>
              <h1 className="animate-fade-up text-white font-serif text-5xl md:text-7xl leading-[1.05] font-medium mb-6" style={{ animationDelay: "0.1s" }}>{tr({ en: "Learn the Ancient Art of Mosaic", tr: "Antik Mozaik Sanatını Öğrenin" })}</h1>
              <p className="animate-fade-up text-white/85 text-[16px] md:text-[18px] leading-relaxed max-w-xl mb-9" style={{ animationDelay: "0.2s" }}>{tr({ en: "HAAS Academy brings thousands of years of craftsmanship to your hands — through immersive online lessons and hands-on projects.", tr: "HAAS Akademi, binlerce yıllık zanaatı sürükleyici çevrimiçi dersler ve uygulamalı projelerle ellerinize taşır." })}</p>
              <a href="#akademi-app" className="animate-fade-up group inline-flex items-center gap-3 bg-[#a67c52] hover:bg-[#8f6844] text-white text-[13px] uppercase tracking-[0.16em] px-8 py-4 transition-colors" style={{ animationDelay: "0.3s" }}>
                {tr({ en: "Explore the Academy", tr: "Akademiyi Keşfet" })} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-24 md:py-28">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-14 items-center">
          <div className="card-img-zoom overflow-hidden">
            <img src="/mosaic/academy/intro.jpg" alt="HAAS Akademi mozaik eğitimi — usta ve öğrenci / mosaic education with master and student" loading="lazy" decoding="async" className="w-full h-[520px] object-cover" />
          </div>
          <div>
            <p className="text-[#a67c52] text-[12px] uppercase tracking-luxe mb-4">{tr({ en: "Where Craft Becomes Knowledge", tr: "Zanaatın Bilgiye Dönüştüğü Yer" })}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.1] text-[#211d18] mb-6">{tr({ en: "A School Rooted in a Living Tradition", tr: "Yaşayan Bir Geleneğe Dayanan Bir Okul" })}</h2>
            <p className="text-[#5a5348] text-[16px] leading-relaxed mb-5">{tr({ en: "From the quarries of Antioch to your studio, HAAS Academy shares the techniques, patience and vision behind every mosaic we create.", tr: "Antakya ocaklarından atölyenize; HAAS Akademi, ürettiğimiz her mozaiğin ardındaki teknikleri, sabrı ve vizyonu paylaşır." })}</p>
            <p className="text-[#5a5348] text-[16px] leading-relaxed">{tr({ en: "Whether you are a complete beginner or a practising artist, our lessons guide you from your first tessera to a finished masterpiece.", tr: "İster tam bir başlangıç seviyesinde olun ister deneyimli bir sanatçı; derslerimiz sizi ilk tesseranızdan tamamlanmış bir başyapıta kadar yönlendirir." })}</p>
          </div>
        </div>
      </section>

      {/* Courses */}
      <section className="pb-24">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[#a67c52] text-[12px] uppercase tracking-luxe mb-3">HAAS {ui.nav.academy}</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#211d18]">{tr({ en: "What You'll Learn", tr: "Ne Öğreneceksiniz" })}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {courses.map((c, i) => (
              <div key={i} className="group bg-white overflow-hidden shadow-sm hover:shadow-xl transition-shadow">
                <div className="card-img-zoom overflow-hidden h-48">
                  <img src={c.image} alt={tr(c.title)} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <span className="inline-flex items-center justify-center w-11 h-11 bg-[#efe7db] text-[#a67c52] mb-4"><c.icon size={20} /></span>
                  <h3 className="font-serif text-xl text-[#211d18] mb-2">{tr(c.title)}</h3>
                  <p className="text-[#5a5348] text-[13px] leading-relaxed">{tr(c.text)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 bg-[#211d18] text-white">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[#e2c9a8] text-[12px] uppercase tracking-luxe mb-3">{tr({ en: "Simple & Rewarding", tr: "Basit ve Doyurucu" })}</p>
            <h2 className="font-serif text-4xl md:text-5xl">{tr({ en: "How It Works", tr: "Nasıl İşliyor" })}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <div key={i} className="text-center px-4">
                <div className="mx-auto w-16 h-16 flex items-center justify-center border border-[#a67c52] text-[#e2c9a8] mb-5"><s.icon size={26} /></div>
                <span className="text-[#a67c52] font-serif text-2xl">0{i + 1}</span>
                <h3 className="font-serif text-2xl mt-1 mb-3">{tr(s.title)}</h3>
                <p className="text-white/65 text-[14px] leading-relaxed max-w-xs mx-auto">{tr(s.text)}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-12 border-t border-white/10">
            {stats.map((st, i) => (
              <div key={i} className="text-center">
                <st.icon size={22} className="mx-auto text-[#a67c52] mb-3" />
                <p className="font-serif text-3xl md:text-4xl text-white">{st.value}</p>
                <p className="text-white/55 text-[12px] uppercase tracking-[0.16em] mt-1">{tr(st.label)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App download CTA (placeholder buttons) */}
      <section id="akademi-app" className="relative py-28 md:py-36 overflow-hidden">
        <img src="/mosaic/2025/08/6.png" alt="HAAS Akademi mozaik montaj süreci / mosaic assembly process" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 35%" }} />
        <div className="absolute inset-0 bg-black/65" />
        <div className="relative z-10 max-w-3xl mx-auto px-5 text-center">
          <p className="text-[#e2c9a8] text-[12px] uppercase tracking-luxe mb-4">{tr({ en: "Coming Soon", tr: "Çok Yakında" })}</p>
          <h2 className="font-serif text-4xl md:text-6xl text-white mb-6">{tr({ en: "Carry the Academy in Your Pocket", tr: "Akademiyi Cebinizde Taşıyın" })}</h2>
          <p className="text-white/85 text-[17px] leading-relaxed mb-10 max-w-xl mx-auto">{tr({ en: "The HAAS Academy app will let you learn anywhere, anytime. Download links are on the way.", tr: "HAAS Akademi uygulamasıyla her yerde, her zaman öğrenebileceksiniz. İndirme bağlantıları çok yakında." })}</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <StoreButton badge={<AppleBadge />} top={tr({ en: "Download on the", tr: "İndir" })} main="App Store" />
            <StoreButton badge={<PlayBadge />} top={tr({ en: "Get it on", tr: "İndir" })} main="Google Play" />
          </div>
          <p className="text-white/45 text-[12px] mt-5">{tr({ en: "* Buttons will be linked once the app is published.", tr: "* Butonlar, uygulama yayınlandığında bağlanacaktır." })}</p>
        </div>
      </section>
    </div>
  );
};

export default Academy;
